import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { addDays, dubaiToday, weekdayMon0, WEEKDAYS } from "@/lib/format";

const CELL = {
  available: "bg-white text-[#0B1F3A] border border-[rgba(20,35,55,0.10)] hover:border-[#0B1F3A]/50 hover:-translate-y-px font-semibold",
  booked: "text-[#697386]/60 line-through decoration-[#697386]/40 cursor-not-allowed",
  unavailable: "text-[#697386]/45 cursor-not-allowed",
  past: "text-[#697386]/25 cursor-not-allowed",
};

const ym = (s) => +s.slice(0, 4) * 12 + +s.slice(5, 7);

function Legend() {
  const items = [
    ["Available", "bg-white border border-[rgba(20,35,55,0.2)]"],
    ["Selected", "bg-[#0B1F3A]"],
    ["Fully booked", "bg-[#697386]/25"],
    ["Unavailable", "bg-transparent border border-dashed border-[#697386]/40"],
  ];
  return (
    <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#697386]" data-testid="calendar-legend">
      {items.map(([l, c]) => (
        <span key={l} className="flex items-center gap-2"><span className={`h-3 w-3 rounded-full ${c}`} />{l}</span>
      ))}
    </div>
  );
}

export function MonthCalendar({ selected, onSelect, horizonDays, refreshKey }) {
  const today = dubaiToday();
  const [view, setView] = useState({ y: +today.slice(0, 4), m: +today.slice(5, 7) });
  const [days, setDays] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let on = true;
    setDays(null);
    setError(false);
    api.get("/availability/month", { params: { year: view.y, month: view.m } })
      .then((r) => on && setDays(r.data.days))
      .catch(() => on && setError(true));
    return () => {
      on = false;
    };
  }, [view, refreshKey]);

  const maxYmd = addDays(today, horizonDays);
  const cur = view.y * 12 + view.m;
  const canPrev = cur > ym(today);
  const canNext = cur < ym(maxYmd);
  const shift = (n) => setView(({ y, m }) => ({ y: m + n > 12 ? y + 1 : m + n < 1 ? y - 1 : y, m: ((m + n + 11) % 12) + 1 }));

  const mm = String(view.m).padStart(2, "0");
  const lead = weekdayMon0(`${view.y}-${mm}-01`);
  const count = new Date(Date.UTC(view.y, view.m, 0)).getUTCDate();
  const label = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(view.y, view.m - 1, 1)));

  return (
    <div data-testid="booking-calendar">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display text-xl font-semibold text-[#172033]" data-testid="calendar-month-label" aria-live="polite">{label}</h3>
        <div className="flex gap-2">
          <button onClick={() => shift(-1)} disabled={!canPrev} className="h-10 w-10 rounded-full grid place-items-center border border-[rgba(20,35,55,0.10)] bg-white/70 hover:bg-white disabled:opacity-30 transition-colors" aria-label="Previous month" data-testid="calendar-prev-month">
            <ChevronLeft size={18} />
          </button>
          <button onClick={() => shift(1)} disabled={!canNext} className="h-10 w-10 rounded-full grid place-items-center border border-[rgba(20,35,55,0.10)] bg-white/70 hover:bg-white disabled:opacity-30 transition-colors" aria-label="Next month" data-testid="calendar-next-month">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2 text-center" role="grid">
        {WEEKDAYS.map((d) => (
          <div key={d} className="text-[11px] font-semibold uppercase tracking-wider text-[#697386] pb-2">{d}</div>
        ))}
        {Array.from({ length: lead }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: count }).map((_, i) => {
          const ymd = `${view.y}-${mm}-${String(i + 1).padStart(2, "0")}`;
          const status = days?.[ymd]?.status || "unavailable";
          const isSel = selected === ymd;
          const enabled = status === "available";
          return (
            <button
              key={ymd}
              disabled={!enabled || !days}
              onClick={() => onSelect(ymd)}
              aria-pressed={isSel}
              aria-label={`${ymd} ${status}`}
              data-testid={`calendar-day-${ymd}`}
              data-status={isSel ? "selected" : status}
              className={`relative aspect-square rounded-2xl text-sm transition-[transform,background-color,border-color] duration-200 ${!days ? "animate-pulse text-[#697386]/30" : isSel ? "bg-[#0B1F3A] text-white font-semibold shadow-lg" : CELL[status]}`}
            >
              {i + 1}
              {ymd === today && <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-[#C7A76C]" />}
            </button>
          );
        })}
      </div>
      {error && <p className="mt-4 text-sm text-[#C2410C]" data-testid="calendar-error">We couldn’t load availability. Please refresh and try again.</p>}
      <Legend />
    </div>
  );
}
